/**
 * Category registry — the schema slice every environment loads
 * (docs/bim-plan/01-architecture.md §4). Geometry generators live in bim-geometry's own
 * registry keyed by the same CategoryId; handle configs live in frontend/src/bim/gizmos.
 */
import type { CategoryId } from './types'
import type { ParamSchema } from './params'

export interface CategorySchema {
  id: CategoryId
  label: string
  typeParams: ParamSchema
  instanceParams: ParamSchema
  usesAssembly?: 'wall' | 'floor' | 'roof' | 'ceiling' | 'stem'
  /** `cutsHost`: the hosted element subtracts an opening from its host (door/window
   *  yes; casework no — it just attaches). P6-clean: pipeline's hosted-opening map
   *  derives from this instead of a hardcoded door||window check. */
  hostable?: { hostsInto: CategoryId[]; cutsHost?: boolean }
  /**
   * P6-clean (docs/bim-plan/08 Part 0): which param holds the element's plan
   * geometry. Declared HERE so shared code (snap candidate collection, host
   * re-anchoring on geometry edits, whole-path translation, corner joins) consults
   * the registry instead of probing param names ad hoc — the audit found the
   * `paramKey === 'path'` hardcode silently broke hosted re-anchoring for any
   * boundary-based host (the skylight-into-roof blocker). Absent = the category has
   * no plan path (point-placed/hosted categories).
   */
  pathParamKey?: 'path' | 'boundary'
  /**
   * P6-clean: pick priority for overlapping candidates (lower wins — a door beats
   * its wall beats the floor under both). Shared by planPick ordering and the 3D
   * PICK_RANK map, which previously hardcoded a category list. Absent = 9.
   */
  pickRank?: number
}

/** Registry-derived category sets (P6-clean) — replaces hand-maintained Set literals. */
export function pathCategories(): Set<CategoryId> {
  return new Set([...REGISTRY.values()].filter((s) => s.pathParamKey).map((s) => s.id))
}
export function hostedCategories(): Set<CategoryId> {
  // hostable AND no plan path of its own (door/window/casework) — railing is
  // hostable but path-based, and its snap geometry comes from the PATH branch
  return new Set([...REGISTRY.values()].filter((s) => s.hostable && !s.pathParamKey).map((s) => s.id))
}
/** The declared plan-geometry param for a category ('path'/'boundary'), or null. */
export function pathParamKeyFor(id: CategoryId): 'path' | 'boundary' | null {
  return REGISTRY.get(id)?.pathParamKey ?? null
}
export function pickRankFor(id: CategoryId): number {
  return REGISTRY.get(id)?.pickRank ?? 9
}
/** Hosted categories that CUT an opening in their host (door/window; not casework). */
export function cuttingHostedCategories(): Set<CategoryId> {
  return new Set([...REGISTRY.values()].filter((s) => s.hostable?.cutsHost).map((s) => s.id))
}

const REGISTRY = new Map<CategoryId, CategorySchema>()

export function registerCategory(schema: CategorySchema): void {
  REGISTRY.set(schema.id, schema)
}
export function getCategory(id: CategoryId): CategorySchema | undefined {
  return REGISTRY.get(id)
}
export function listCategories(): CategorySchema[] {
  return [...REGISTRY.values()]
}
